import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  EndUser,
  EndUserUpdate,
  EndUserPasswordUpdate,
  EndUserSubscription,
  EndUserCheckoutCreateRequest,
  EndUserCheckoutCreateResponse,
  EndUserSubscriptionCancelRequest,
  EndUserTransactionListResponse
} from '../types/endUser';
import { PlanListResponse } from '../types/billing';
import {
  isPasswordChangeRequiredResponse,
  notifyPasswordChangeRequired
} from '../utils/portalAuthErrors';
import {
  AccountEmployee,
  AccountSubscription,
  AccountUser,
  AccountUserCheck,
  AccountUserCreateRequest,
  AccountUserCreatedResponse,
  ConsumptionRefreshResponse,
  CreditsHistoryQuery,
  CreditsPeriod,
  CustomerAccountSummary,
  CustomerCreditsHistoryResponse,
  CustomerCreditsResponse,
  CustomerHireEmployeeRequest,
  EmployeeStorageRequest,
  EmployeeStorageResponse,
  EmployeeUpdate
} from '../types/clientAccount';
import {
  CreditProjection,
  CustomerPackages,
  PurchaseIntentResponse
} from '../types/creditPackage';

// Token de IMPERSONACIÓN (partner «viendo como cliente», solo lectura, 15 min).
// Va en sessionStorage (por-pestaña) para NO pisar una sesión real de cliente
// en otras pestañas del mismo navegador.
export const CUSTOMER_IMPERSONATION_KEY = 'customer_impersonation_token';

class CustomerApiService {
  private api: AxiosInstance;
  private authToken: string | null = null;
  private impersonation = false;

  constructor() {
    const baseURL = window.location.protocol === 'https:'
      ? `https://${window.location.host}/api/v1/customer`
      : '/api/v1/customer';

    this.api = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // El token de impersonación (por-pestaña) tiene prioridad sobre la sesión
    // normal de localStorage: en esa pestaña se ve el portal como el cliente.
    const impersonationToken = sessionStorage.getItem(CUSTOMER_IMPERSONATION_KEY);
    if (impersonationToken) {
      this.setImpersonationToken(impersonationToken);
    } else {
      // Load token from localStorage
      this.authToken = localStorage.getItem('customer_auth_token');
      if (this.authToken) {
        this.setAuthToken(this.authToken);
      }
    }

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        console.log(`Customer API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('Customer API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        console.log(`Customer API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error: AxiosError) => {
        console.error('Customer API Response Error:', error);

        // Veto en servidor por contraseña de un solo uso (403
        // PASSWORD_CHANGE_REQUIRED): la sesión es VÁLIDA, así que ni se limpia
        // el token ni se va al login (eso sería un bucle: el usuario volvería a
        // entrar con la misma temporal). Se avisa al contexto para que levante
        // el gate y se deja pasar el error a quien llamó.
        if (isPasswordChangeRequiredResponse(error.response)) {
          notifyPasswordChangeRequired('customer');
          return Promise.reject(error.response?.data || error.message);
        }

        // Handle 401 errors by clearing auth and redirecting to login
        if (error.response?.status === 401) {
          console.log('Customer auth expired or invalid - clearing auth and redirecting to login');
          this.clearAuth();
          
          // Only redirect if we're in a customer route
          if (window.location.pathname.startsWith('/customer')) {
            window.location.href = '/customer/login';
          }
        }
        
        return Promise.reject(error.response?.data || error.message);
      }
    );
  }

  setAuthToken(token: string) {
    this.authToken = token;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('customer_auth_token', token);
  }

  // Instala el token de impersonación SOLO en memoria/cabecera (nunca en
  // localStorage): la sesión impersonada vive y muere con la pestaña.
  setImpersonationToken(token: string) {
    this.authToken = token;
    this.impersonation = true;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  isImpersonation(): boolean {
    return this.impersonation;
  }

  clearAuth() {
    this.authToken = null;
    this.impersonation = false;
    delete this.api.defaults.headers.common['Authorization'];
    localStorage.removeItem('customer_auth_token');
    localStorage.removeItem('customer_user');
    sessionStorage.removeItem(CUSTOMER_IMPERSONATION_KEY);
  }

  // Profile endpoints
  async getMe(): Promise<EndUser> {
    const response = await this.api.get<EndUser>('/auth/me');
    return response.data;
  }

  // /auth/me tolerante para el modo impersonación: un token solo-lectura puede
  // no exponer el perfil (401/403/404). validateStatus evita que el interceptor
  // lo trate como sesión caducada (limpiaría y redirigiría al login).
  async getMeTolerant(): Promise<EndUser | null> {
    const response = await this.api.get<EndUser>('/auth/me', {
      validateStatus: (status) => status === 200 || status === 401 || status === 403 || status === 404
    });
    return response.status === 200 ? response.data : null;
  }

  async updateMe(data: EndUserUpdate): Promise<EndUser> {
    const response = await this.api.put<EndUser>('/auth/me', data);
    return response.data;
  }

  async changePassword(data: EndUserPasswordUpdate): Promise<{ message: string }> {
    const response = await this.api.post('/auth/change-password', data);
    return response.data;
  }

  // Billing endpoints
  async getBillingConfig(): Promise<{ paddle_client_token: string; paddle_environment: string }> {
    const response = await this.api.get<{ paddle_client_token: string; paddle_environment: string }>('/billing/config');
    return response.data;
  }

  async getPlans(activeOnly: boolean = true): Promise<PlanListResponse> {
    const response = await this.api.get<PlanListResponse>('/billing/plans', {
      params: { active_only: activeOnly }
    });
    return response.data;
  }

  async getMySubscription(): Promise<EndUserSubscription> {
    const response = await this.api.get<EndUserSubscription>('/billing/subscription');
    return response.data;
  }

  async getCurrentSubscription(): Promise<EndUserSubscription> {
    return this.getMySubscription();
  }

  async createCheckout(request: EndUserCheckoutCreateRequest): Promise<EndUserCheckoutCreateResponse> {
    const response = await this.api.post<EndUserCheckoutCreateResponse>('/billing/checkout', request);
    return response.data;
  }

  async cancelSubscription(request: EndUserSubscriptionCancelRequest): Promise<any> {
    const response = await this.api.post('/billing/subscription/cancel', request);
    return response.data;
  }

  async resumeSubscription(): Promise<any> {
    const response = await this.api.post('/billing/subscription/resume');
    return response.data;
  }

  async deleteSubscription(subscriptionId: number): Promise<any> {
    const response = await this.api.delete(`/billing/subscription/${subscriptionId}`);
    return response.data;
  }

  async getSubscriptionHistory(limit: number = 10): Promise<EndUserSubscription[]> {
    const response = await this.api.get<EndUserSubscription[]>('/billing/subscription/history', {
      params: { limit }
    });
    return response.data;
  }

  async getTransactions(limit: number = 50): Promise<EndUserTransactionListResponse> {
    const response = await this.api.get<EndUserTransactionListResponse>('/billing/transactions', {
      params: { limit }
    });
    return response.data;
  }

  // Mi cuenta de empleados IA (fase 1, sin pasarela)
  // 404 = el EndUser no tiene cuenta → devolvemos null (la UI muestra estado vacío,
  // nunca rompe). validateStatus evita que el interceptor lo convierta en rechazo.
  // El summary viene ampliado con los campos «dos bolsas» del plan de créditos
  // (plan_total/plan_used/plan_remaining/plan_renews_at/extra_balance/
  // available_total) — opcionales mientras el backend está en construcción.
  // También trae managed_by_reseller {name, contact_email} | null: si la cuenta
  // la gestiona un partner, el portal oculta billing/planes Paddle y muestra
  // el banner de cuenta gestionada.
  async getMyAccount(): Promise<CustomerAccountSummary | null> {
    const response = await this.api.get<CustomerAccountSummary>('/account', {
      validateStatus: (status) => status === 200 || status === 404
    });
    return response.status === 404 ? null : response.data;
  }

  // Mis empleados desplegados con su estado de actualización (¿versión superior en el
  // marketplace?). Best-effort en la UI: si falla, no rompe la página (banner sin datos).
  async getMyEmployeeUpdates(): Promise<EmployeeUpdate[]> {
    const response = await this.api.get<EmployeeUpdate[]>('/account/employee-updates');
    return response.data;
  }

  // Sube uno de mis empleados desplegados a la última versión del marketplace (re-instala
  // en el tenant). 409 si ya está al día / no desplegado.
  async upgradeMyEmployee(employeeId: number): Promise<{ latest_version: string }> {
    const response = await this.api.post(`/account/employees/${employeeId}/upgrade`);
    return response.data;
  }

  // El PLAN de créditos de la cuenta como «suscripción» (has_plan, créditos,
  // renews_at, source, managed_by). Reconvierte la vieja vista de suscripciones
  // Paddle a este producto. 404 (usuario sin cuenta) → null, como getMyAccount.
  async getAccountSubscription(): Promise<AccountSubscription | null> {
    const response = await this.api.get<AccountSubscription>('/account/subscription', {
      validateStatus: (status) => status === 200 || status === 404
    });
    return response.status === 404 ? null : response.data;
  }

  async getMyAccountEmployees(): Promise<AccountEmployee[]> {
    const response = await this.api.get<AccountEmployee[]>('/account/employees');
    return response.data;
  }

  async getMyAccountUsers(): Promise<AccountUser[]> {
    const response = await this.api.get<AccountUser[]>('/account/users');
    return response.data;
  }

  // Alta de usuario. auth_method decide cómo entrará (contraseña / contraseña
  // y Google / solo Google): con solo-Google la respuesta NO trae contraseña.
  // Si el usuario ya existía en el tenant, tampoco (created=false): solo se le
  // añade a la cuenta y al proyecto.
  async addAccountUser(data: AccountUserCreateRequest): Promise<AccountUserCreatedResponse> {
    const response = await this.api.post<AccountUserCreatedResponse>('/account/users', data);
    return response.data;
  }

  // Comprobación previa del email (al perder el foco en el diálogo). Nunca
  // rompe el alta: si el backend/tenant no responde devolvemos checked=false
  // y el portal simplemente no enseña aviso.
  async checkAccountUserEmail(email: string): Promise<AccountUserCheck> {
    try {
      const response = await this.api.get<AccountUserCheck>('/account/users/check', {
        params: { email }
      });
      return response.data;
    } catch (err) {
      console.warn('users/check no disponible:', err);
      return { email, checked: false, exists: false, in_account: false };
    }
  }

  async assignAccountUser(
    userId: number,
    data: { account_employee_id: number; role: 'member' | 'admin' }
  ): Promise<{ assignment_id: number; role: string }> {
    const response = await this.api.post(`/account/users/${userId}/assign`, data);
    return response.data;
  }

  // Quitar la asignación usuario↔empleado (la X del chip en la tabla Usuarios).
  // Los 4xx llegan como {detail} vía el interceptor y la UI los enseña por toast.
  async unassignUser(userId: number, accountEmployeeId: number): Promise<void> {
    await this.api.delete(`/account/users/${userId}/assign/${accountEmployeeId}`);
  }

  // También ampliado con los campos «dos bolsas» (ver getMyAccount).
  // period filtra movimientos y consumo a la ventana (ciclo vigente / últimos
  // 30 días / todo); sin argumento = todo (comportamiento clásico).
  async getMyAccountCredits(period?: CreditsPeriod): Promise<CustomerCreditsResponse> {
    const response = await this.api.get<CustomerCreditsResponse>('/account/credits', {
      params: period ? { period } : undefined
    });
    return response.data;
  }

  // Historial COMPLETO del ledger, paginado/filtrado/ordenado en servidor — lo
  // consume la página propia «Historial de créditos». `kind` (varios) viaja como
  // lista separada por comas, que es lo que el backend espera; el resto de
  // filtros van tal cual. Devuelve {items, total, limit, offset, ...} + el
  // desglose de bolsas para pintar las tres cifras junto a la tabla.
  async getMyAccountCreditsHistory(
    query: CreditsHistoryQuery = {}
  ): Promise<CustomerCreditsHistoryResponse> {
    const params: Record<string, string | number> = {};
    if (query.limit != null) params.limit = query.limit;
    if (query.offset != null) params.offset = query.offset;
    if (query.kind && query.kind.length > 0) params.kind = query.kind.join(',');
    if (query.bucket) params.bucket = query.bucket;
    if (query.employee_id != null) params.employee_id = query.employee_id;
    if (query.period) params.period = query.period;
    if (query.date_from) params.date_from = query.date_from;
    if (query.date_to) params.date_to = query.date_to;
    if (query.sort) params.sort = query.sort;
    if (query.order) params.order = query.order;
    const response = await this.api.get<CustomerCreditsHistoryResponse>(
      '/account/credits/history',
      { params }
    );
    return response.data;
  }

  // Contratar un empleado IA desde el portal. 409 con detail si supera el cupo
  // o no hay saldo (el backend es la autoridad). El límite mensual opcional NO
  // se compara con el saldo: no es una transferencia.
  async hireEmployee(data: CustomerHireEmployeeRequest): Promise<{ account_employee_id: number; status: string }> {
    const response = await this.api.post<{ account_employee_id: number; status: string }>('/account/employees', data);
    return response.data;
  }

  // Fijar el LÍMITE mensual de gasto de un empleado (se aplica al (re)desplegarlo).
  // No resta saldo: la suma de límites puede superar el saldo de la cuenta.
  async allocateCredits(employeeId: number, credits: number): Promise<void> {
    await this.api.post(`/account/employees/${employeeId}/allocate`, { credits });
  }

  // AMPLIAR el almacenamiento de un empleado (valores TOTALES de cada volumen).
  // Solo aumenta y es PERMANENTE mientras el empleado exista: sus GiB cuentan
  // para el uso de la cuenta y, por tanto, para su cargo por ciclo. 409 con
  // detail si baja, no cuadra el tramo, o supera techo/saldo. La respuesta trae
  // `sync` con lo que pasó con el DISCO REAL (applied/deferred/failed): un 2xx
  // NO garantiza que el volumen haya crecido, así que hay que mirarlo antes de
  // cantar victoria.
  async expandEmployeeStorage(
    employeeId: number,
    data: EmployeeStorageRequest
  ): Promise<EmployeeStorageResponse> {
    const response = await this.api.post(`/account/employees/${employeeId}/storage`, data);
    return response.data;
  }

  // Dar de baja un empleado: retira su app del tenant y LIBERA el cupo.
  // Irreversible. `tenant_error` ≠ null = la baja se hizo pero el tenant no
  // pudo retirar la app (el portal lo avisa; el cupo queda libre igualmente).
  async removeEmployee(employeeId: number): Promise<{ removed: boolean; tenant_error: string | null }> {
    const response = await this.api.delete(`/account/employees/${employeeId}`);
    return response.data;
  }

  // Refresco FORZADO del consumo (salta el throttle de 5 min del GET).
  async refreshConsumption(): Promise<ConsumptionRefreshResponse> {
    const response = await this.api.post<ConsumptionRefreshResponse>('/account/sync-consumption');
    return response.data;
  }

  // ── Paquetes de créditos (el catálogo del producto «empleados IA») ────────
  // Sustituye a getPlans() en el portal: aquella devuelve la tabla `plans`
  // (suscripciones B2C de tenants por Paddle), que es otro producto.

  async getCreditPackages(): Promise<CustomerPackages> {
    const response = await this.api.get<CustomerPackages>('/account/packages');
    return response.data;
  }

  // ⚠ NO COBRA (fase actual sin pasarela): registra la intención y devuelve
  // `status='pending_gateway'` + a quién dirigirse mientras tanto. La UI tiene
  // que contarlo tal cual — nunca como una compra hecha.
  async purchaseCreditPackage(packageId: number): Promise<PurchaseIntentResponse> {
    const response = await this.api.post<PurchaseIntentResponse>('/account/packages/purchase', {
      package_id: packageId
    });
    return response.data;
  }

  // Proyección de saldo. `available=false` ⇒ no hay histórico suficiente y la
  // UI no pinta nada (mejor nada que una fecha que baila).
  async getCreditProjection(): Promise<CreditProjection> {
    const response = await this.api.get<CreditProjection>('/account/projection');
    return response.data;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.authToken;
  }
}

export const customerApiService = new CustomerApiService();
export default customerApiService;

